import {
  ActionList,
  ActionListItem,
  Divider,
  GridItem,
  Text,
  FormFeedback,
} from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { useField } from "formik";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { InstitutionInputModal } from "./InstitutionInputModal";
import { SingleInputModal } from "./SingleInputModal";
import { ApplicationContext } from "../../Application.context";

type SocialMediaCheckField = components["schemas"]["SocialMediaCheckField"];

export function SocialMedialInputField({
  field: config,
}: {
  field: SocialMediaCheckField;
}) {
  const { t } = useTranslation();
  const [, meta, helpers] = useField(config.name);
  const { saveData, currentSectionKey } = useContext(ApplicationContext);

  const handleChange = (name: string) => async (value: string) => {
    await helpers.setValue({
      ...meta.value,
      [name]: value,
    });

    await saveData({
      sectionKey: currentSectionKey,
      skipValidation: true,
    });
  };

  const errors = meta.error as unknown as Record<string, string> | null;

  return (
    <>
      <GridItem>
        <ActionList>
          <ActionListItem
            displayAccessibility
            title="Facebook"
            trailing={
              <SingleInputModal
                testID="social-media-facebook"
                type="Facebook"
                label={t("Facebook username or URL")}
                value={meta.value?.facebook_url ?? ""}
                onChange={handleChange("facebook_url")}
              />
            }
          >
            {meta.value?.facebook_url && (
              <Text style="form/body-secondary" color="text-weak">
                {meta.value.facebook_url}
              </Text>
            )}
          </ActionListItem>

          <Divider />

          <ActionListItem
            displayAccessibility
            title="Instagram"
            trailing={
              <SingleInputModal
                testID="social-media-instagram"
                type="Instagram"
                label={t("Instagram username or URL")}
                value={meta.value?.instagram_url ?? ""}
                onChange={handleChange("instagram_url")}
              />
            }
          >
            {meta.value?.instagram_url && (
              <Text style="form/body-secondary" color="text-weak">
                {meta.value.instagram_url}
              </Text>
            )}
          </ActionListItem>

          <Divider />

          <ActionListItem
            displayAccessibility
            title="LinkedIn"
            trailing={
              <SingleInputModal
                testID="social-media-linkedin"
                type="LinkedIn"
                label={t("LinkedIn username or URL")}
                value={meta.value?.linkedin_url ?? ""}
                onChange={handleChange("linkedin_url")}
              />
            }
          >
            {meta.value?.linkedin_url && (
              <Text style="form/body-secondary" color="text-weak">
                {meta.value.linkedin_url}
              </Text>
            )}
          </ActionListItem>

          <Divider />

          <ActionListItem
            displayAccessibility
            title="TikTok"
            trailing={
              <SingleInputModal
                testID="social-media-tiktok"
                type="TikTok"
                label={t("TikTok username or URL")}
                value={meta.value?.tiktok_url ?? ""}
                onChange={handleChange("tiktok_url")}
              />
            }
          >
            {meta.value?.tiktok_url && (
              <Text style="form/body-secondary" color="text-weak">
                {meta.value.tiktok_url}
              </Text>
            )}
          </ActionListItem>

          <Divider />

          <ActionListItem
            displayAccessibility
            title={t("X (formerly Twitter)")}
            testID="social-media-x"
            trailing={
              <SingleInputModal
                type="X"
                label={t("X username or URL")}
                value={meta.value?.twitter_url ?? ""}
                onChange={handleChange("twitter_url")}
              />
            }
          >
            {meta.value?.twitter_url && (
              <Text style="form/body-secondary" color="text-weak">
                {meta.value.twitter_url}
              </Text>
            )}
          </ActionListItem>

          <Divider />

          <ActionListItem
            displayAccessibility
            title={t("High school")}
            trailing={
              <SingleInputModal
                testID="social-media-high-school"
                type="high school"
                label={t("High school name")}
                value={meta.value?.high_school_name ?? ""}
                onChange={handleChange("high_school_name")}
                isRequired
              />
            }
          >
            {meta.value?.high_school_name ? (
              <Text style="form/body-secondary" color="text-weak">
                {meta.value.high_school_name}
              </Text>
            ) : (
              errors?.high_school_name && (
                <FormFeedback type="invalid">
                  {errors?.high_school_name}
                </FormFeedback>
              )
            )}
          </ActionListItem>

          <Divider />

          <ActionListItem
            displayAccessibility
            title={t("Post secondary institution")}
            trailing={
              <InstitutionInputModal
                value={{
                  college_country: meta.value?.college_country ?? "",
                  college_name: meta.value?.college_name ?? "",
                }}
                onChange={({ college_country, college_name }) =>
                  helpers.setValue({
                    ...meta.value,
                    college_country: college_name && college_country,
                    college_name,
                  })
                }
              />
            }
          >
            {meta.value?.college_name && (
              <Text style="form/body-secondary" color="text-weak">
                {meta.value.college_name}
              </Text>
            )}
          </ActionListItem>
        </ActionList>
      </GridItem>
    </>
  );
}
